import { CARD_SPHERE_BODY_BPS } from '@kenocyber/game-domain';
import type Phaser from 'phaser';

/**
 * **A Esfera Ígnea e o Catolé: o voo e as duas explosões** (gate V28H.T).
 *
 * ## Porque este ficheiro existe
 *
 * As duas cartas foram reprovadas por não aparecerem, e a causa não era o
 * desenho - era a cadeia. Elas rebentavam no instante da ordem e a única coisa
 * que produziam era um EVENTO; os eventos não entram na instantânea, e na caça o
 * cliente adopta a instantânea do servidor em vez de re-executar a ordem. O
 * evento nascia no servidor e morria lá.
 *
 * Agora elas VIAJAM. O corpo em voo é estado da simulação, portanto o cliente
 * recebe-o e desenha-o a cada quadro; a explosão nasce no cliente, quando a
 * bomba chega - que é o mesmo caminho de um tiro normal.
 *
 * ## O que se desenha, e porquê assim
 *
 * A **esfera** cresce a caminho: a descrição aprovada diz "uma grande esfera de
 * fogo", e uma esfera que nascesse já do tamanho final não teria como se
 * distinguir de um tiro grande.
 *
 * O **Catolé** é um corpo com anel de guia, mais lento e mais pesado, e a
 * explosão dele é uma coisa **diferente** e não a mesma maior: seis vezes o raio
 * da Bomba Atômica é a maior área do jogo, e uma onda com anéis concêntricos lê
 * essa dimensão de uma maneira que um clarão não lê.
 *
 * ## O orçamento
 *
 * Texturas procedurais criadas uma vez; corpos e explosões saem de conjuntos
 * FIXOS e são escondidos em vez de destruídos. `update` não aloca. `destroy()`
 * esvazia tudo, e a cena chama-o no `SHUTDOWN`.
 *
 * `blendAdd` chega por parâmetro em vez de um `import` do motor, como em
 * `card-fx.ts` e `pvp-arena-vfx.ts`: importar `phaser` como VALOR arranca o
 * registo de renderers inteiro, que num ambiente sem canvas rebenta antes de a
 * primeira prova correr.
 */

const TEXTURE = {
  fireballCore: 'card-blast-fireball-core',
  fireballTongues: 'card-blast-fireball-tongues',
  fireballTonguesInner: 'card-blast-fireball-tongues-inner',
  fireballRays: 'card-blast-fireball-rays',
  fireballGlow: 'card-blast-fireball-glow',
  catoleBody: 'card-blast-catole-missile',
  catoleFlame: 'card-blast-catole-flame',
  shock: 'card-blast-shock',
  ember: 'card-blast-ember',
  iceSphere: 'card-blast-ice-sphere',
  iceStar: 'card-blast-ice-star',
  iceShard: 'card-blast-ice-shard',
} as const;

/**
 * **Os quadros do raio** (gate V28H.X): a mesma descarga, redesenhada.
 *
 * Uma textura só não chega, e o dono disse-o depois de a ver: "a electricidade
 * do raio está muito estática, o movimento do raio praticamente não existe".
 * Tinha razão - uma linha partida assada uma vez é um DESENHO de um raio, e um
 * raio é uma coisa que se refaz dezenas de vezes por segundo. O que se via era
 * um ziguezague rígido a deslizar pela arena.
 *
 * Seis chapas com sementes diferentes, trocadas a cada 40 ms: a linha parte-se
 * noutro sítio, as farpas mudam de lado, e a descarga passa a crepitar. Seis e
 * não duas porque duas leem-se como um piscar de dois estados; e não vinte,
 * porque a partir de meia dúzia ninguém distingue a sequência.
 *
 * Assadas uma vez por cena, como todas as outras: o custo é de arranque.
 */
const SHOCK_FRAMES = 6;
const SHOCK_FRAME_MS = 40;

const SHOCK_KEYS: readonly string[] = Array.from(
  { length: SHOCK_FRAMES },
  (_unused, index) => `card-blast-shock-bolt-${index}`,
);

/**
 * **O modo de mistura NORMAL, escrito e não importado** (gate V28H.V).
 *
 * `Phaser.BlendModes.NORMAL` é zero, e a razão para o escrever aqui é a mesma
 * pela qual `blendAdd` chega por parâmetro: importar `phaser` como VALOR
 * arranca o registo de renderers inteiro, que num ambiente sem canvas rebenta
 * antes de a primeira prova correr.
 *
 * O míssil precisa dele. Ele é metal pintado, e somar luz transformaria o
 * escuro em transparente - exactamente o contrário do volume que a textura
 * assou. É a mesma escolha que o Núcleo Atômico já tinha feito.
 */
const BLEND_NORMAL = 0;

/** Quantos corpos e quantas explosões o conjunto guarda. */
const BODY_POOL = 6;
const BURST_POOL = 4;
/** E quantas brasas cada explosão espalha. */
const EMBERS = 10;

/**
 * **As proporções do míssil**, em múltiplos do diâmetro do corpo mecânico.
 *
 * Comprido três raios e largo menos de um: a largura cabe DENTRO do círculo que
 * colide, e é isso que impede a silhueta de prometer um alcance que ela não tem.
 * O comprimento passa - um míssil é comprido, e um comprimento contido no
 * círculo daria um charuto curto que se lê como uma cápsula.
 */
/**
 * A que fracção do raio da textura começa o anel das línguas, em basis points.
 *
 * Lida pelos dois lados - por quem assa a textura e por quem a desenha - para o
 * anel interior cair exactamente sobre a borda da esfera. Dois números seriam
 * duas oportunidades de a coroa nascer dentro da bola outra vez.
 */
const CORONA_RING_BPS = 5_500;

/**
 * E o anel dos raios LONGOS, mais apertado (gate V28I.B).
 *
 * Quarenta e dois por cento contra cinquenta e cinco: a mesma ponta a nascer
 * mais perto do centro da textura é uma agulha mais comprida, e - porque quem
 * desenha divide por esta fracção - a raiz continua a cair sobre a borda da
 * esfera. É assim que os raios ficam longos sem se descolarem dela.
 */
const RAY_RING_BPS = 4_200;

const MISSILE_LENGTH = 1.5;
const MISSILE_WIDTH = 0.45;

/** Quanto tempo uma explosão leva a abrir e apagar, em ms. */
const BURST_MS = 620;

/** A esfera nasce a esta fracção do tamanho final, e cresce até um. */
const FIREBALL_BIRTH = 0.35;
/** E leva este tempo a chegar ao tamanho cheio, em ms de voo. */
const FIREBALL_GROW_MS = 700;

function makeRadialGlow(
  scene: Phaser.Scene,
  key: string,
  stops: readonly (readonly [number, string])[],
  size = 256,
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const gradient = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  for (const [offset, colour] of stops) {
    gradient.addColorStop(offset, colour);
  }
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/**
 * A onda de choque do Catolé: anéis CONCÊNTRICOS e não um disco.
 *
 * A diferença é o que faz seis vezes o raio da Bomba ler-se como seis vezes:
 * um disco grande é só um clarão maior, e três anéis dão ao olho uma medida de
 * quanto ele cresceu.
 */
function makeShockTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 512;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const outer = middle - 6;
  /* O corpo, quente no meio e transparente na borda. */
  const body = context.createRadialGradient(middle, middle, 0, middle, middle, outer);
  body.addColorStop(0, 'rgba(255,255,255,0.85)');
  body.addColorStop(0.22, 'rgba(255,196,90,0.55)');
  body.addColorStop(0.62, 'rgba(255,110,40,0.28)');
  body.addColorStop(1, 'rgba(180,40,10,0)');
  context.fillStyle = body;
  context.fillRect(0, 0, size, size);
  /* E os três anéis, que são o que dá a medida. */
  context.lineCap = 'round';
  for (const [factor, width, alpha] of [
    [0.98, 7, 0.9],
    [0.74, 5, 0.55],
    [0.5, 4, 0.35],
  ] as const) {
    context.strokeStyle = `rgba(255,226,170,${alpha})`;
    context.lineWidth = width;
    context.beginPath();
    context.arc(middle, middle, outer * factor, 0, Math.PI * 2);
    context.stroke();
  }
  texture.refresh();
}

/**
 * **O Catolé: um míssil balístico intercontinental, enorme.**
 *
 * ## Porque deixou de ser uma bola de fogo
 *
 * Porque o dono o viu voar e disse-o: "a bomba atómica está com um efeito de
 * bola de fogo, deve parecer um míssil balístico intercontinental enorme, e não
 * outra esfera de fogo". Ele partilhava a arte com a Esfera Ígnea - as duas eram
 * um degradê radial quente - e duas cartas diferentes liam-se como a mesma
 * coisa a tamanhos diferentes. A explosão, essa, ficou como estava: foi
 * aprovada.
 *
 * ## Como o volume é feito, sem um motor 3D
 *
 * Pelo mesmo caminho do Núcleo Atômico: a ILUMINAÇÃO é assada na textura. Um
 * degradê vertical ao longo do corpo é o perfil de um cilindro visto de lado - o
 * topo apanha a luz, a barriga cai para a sombra - e é isso, e não uma malha,
 * que faz um rectângulo ler-se como um tubo.
 *
 * As camadas, e a ordem:
 *
 * 1. **aletas traseiras**, primeiro, para o corpo as tapar em parte e elas
 *    ficarem ATRÁS - que é o que dá profundidade;
 * 2. **o corpo**, com o degradê do cilindro;
 * 3. **a ogiva**, um cone com uma ponta escura;
 * 4. **as faixas**, duas vermelhas e uma clara, que são o que dá a ESCALA: sem
 *    marcas na fuselagem, um cilindro grande e um pequeno são idênticos;
 * 5. **as linhas de painel e o brilho**, que assentam a leitura de metal.
 *
 * Desenhado com o nariz para a DIREITA, porque quem o desenha roda-o pelo rumo
 * a que ele viaja - e o ângulo zero de um sprite aponta para a direita.
 */
function makeMissileTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 512;
  const height = 160;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  const midY = height / 2;
  /* O corpo ocupa a parte de trás; a ogiva ocupa o quarto da frente. */
  const bodyLeft = 96;
  const bodyRight = 372;
  const noseTip = 496;
  const radius = 34;

  /* 1. As aletas: dois pares, o de trás maior. Pretas, com aresta acesa. */
  context.fillStyle = 'rgba(16,17,20,1)';
  const fins = [
    [bodyLeft + 6, 74, 56],
    [bodyRight - 96, 52, 34],
  ] as const;
  for (const [x, span, depth] of fins) {
    for (const side of [-1, 1] as const) {
      context.beginPath();
      context.moveTo(x, midY + side * (radius - 4));
      context.lineTo(x + span, midY + side * (radius - 4));
      context.lineTo(x + span - 18, midY + side * (radius + depth));
      context.lineTo(x - 4, midY + side * (radius + depth));
      context.closePath();
      context.fill();
    }
  }
  /*
   * A borda de ataque, num cinzento frio: sem ela, uma aleta preta sobre um
   * fundo preto é um buraco. É a mesma razão pela qual o casco preto leva um
   * brilho especular - o preto só se lê pelas arestas.
   */
  context.strokeStyle = 'rgba(126,134,148,0.75)';
  context.lineWidth = 3;
  for (const [x, span, depth] of fins) {
    for (const side of [-1, 1] as const) {
      context.beginPath();
      context.moveTo(x + span, midY + side * (radius - 4));
      context.lineTo(x + span - 18, midY + side * (radius + depth));
      context.stroke();
    }
  }

  /*
   * 2. O corpo, PRETO, com o degradê que o faz um cilindro.
   *
   * Decisão do dono (gate V28H.W): "o míssil da bomba deve ser preto". O
   * degradê continua a ser o mesmo perfil de cilindro - topo iluminado,
   * barriga em sombra - só que agora entre grafite e preto, e não entre branco
   * e cinzento. Sem ele, um rectângulo preto é um rectângulo preto.
   */
  const skin = context.createLinearGradient(0, midY - radius, 0, midY + radius);
  skin.addColorStop(0, 'rgba(58,62,70,1)');
  skin.addColorStop(0.28, 'rgba(38,41,47,1)');
  skin.addColorStop(0.55, 'rgba(20,21,25,1)');
  skin.addColorStop(1, 'rgba(6,6,8,1)');
  context.fillStyle = skin;
  context.fillRect(bodyLeft, midY - radius, bodyRight - bodyLeft, radius * 2);

  /* 3. A ogiva: o mesmo degradê, a fechar num cone, com a ponta cinzenta. */
  context.beginPath();
  context.moveTo(bodyRight, midY - radius);
  context.quadraticCurveTo(noseTip - 40, midY - radius * 0.62, noseTip, midY);
  context.quadraticCurveTo(noseTip - 40, midY + radius * 0.62, bodyRight, midY + radius);
  context.closePath();
  context.fillStyle = skin;
  context.fill();
  /*
   * A ponta CLARA, e não escura como estava: num míssil preto é o contrário
   * que se lê. O escudo térmico de grafite é a coisa mais luminosa da peça, e é
   * ele que diz para onde ela aponta.
   */
  context.beginPath();
  context.moveTo(noseTip - 54, midY - radius * 0.5);
  context.quadraticCurveTo(noseTip - 20, midY - radius * 0.34, noseTip, midY);
  context.quadraticCurveTo(noseTip - 20, midY + radius * 0.34, noseTip - 54, midY + radius * 0.5);
  context.closePath();
  context.fillStyle = 'rgba(150,157,170,0.95)';
  context.fill();

  /*
   * 4. **A PLACA AMARELA de radioativo**, que é o que diz o que ele carrega.
   *
   * Pedida pelo dono (gate V28H.W), e ela faz duas coisas de uma vez: dá a
   * escala - sem uma marca na fuselagem, um cilindro preto grande e um pequeno
   * são idênticos - e diz o que vai lá dentro sem uma palavra escrita.
   *
   * O trifólio é desenhado e não escrito: são três lâminas de sessenta graus a
   * partir de um núcleo, com sessenta graus de vazio entre elas. É o símbolo
   * real, e desenhá-lo com arcos custa menos do que um ficheiro.
   */
  const plateW = 88;
  const plateH = radius * 1.62;
  const plateX = bodyLeft + 118;
  const plateY = midY - plateH / 2;
  context.fillStyle = 'rgba(246,206,26,1)';
  context.fillRect(plateX, plateY, plateW, plateH);
  /* A moldura escura: ela assenta a placa no casco em vez de a deixar a flutuar. */
  context.strokeStyle = 'rgba(12,12,14,0.9)';
  context.lineWidth = 3;
  context.strokeRect(plateX + 1.5, plateY + 1.5, plateW - 3, plateH - 3);
  const trefoilX = plateX + plateW / 2;
  const trefoilR = plateH * 0.4;
  context.fillStyle = 'rgba(12,12,14,1)';
  for (const turn of [0, 1, 2]) {
    const from = -Math.PI / 2 + (turn * 2 * Math.PI) / 3 - Math.PI / 6;
    const to = from + Math.PI / 3;
    context.beginPath();
    context.moveTo(trefoilX, midY);
    context.arc(trefoilX, midY, trefoilR, from, to);
    context.closePath();
    context.fill();
  }
  /* O núcleo, e o anel branco à volta dele: é assim que o símbolo é. */
  context.beginPath();
  context.arc(trefoilX, midY, trefoilR * 0.34, 0, Math.PI * 2);
  context.fillStyle = 'rgba(246,206,26,1)';
  context.fill();
  context.beginPath();
  context.arc(trefoilX, midY, trefoilR * 0.17, 0, Math.PI * 2);
  context.fillStyle = 'rgba(12,12,14,1)';
  context.fill();

  /* 5. Duas faixas amarelas de aviso, para a escala se ler ao longo do corpo. */
  for (const [x, span] of [
    [bodyRight - 62, 14],
    [bodyRight - 40, 6],
  ] as const) {
    context.fillStyle = 'rgba(246,206,26,0.92)';
    context.fillRect(x, midY - radius, span, radius * 2);
  }

  /* E a sombra do cilindro POR CIMA de tudo, para as marcas curvarem com ele. */
  const overlay = context.createLinearGradient(0, midY - radius, 0, midY + radius);
  overlay.addColorStop(0, 'rgba(0,0,0,0.3)');
  overlay.addColorStop(0.34, 'rgba(0,0,0,0)');
  overlay.addColorStop(0.62, 'rgba(0,0,0,0.12)');
  overlay.addColorStop(1, 'rgba(0,0,0,0.55)');
  context.fillStyle = overlay;
  context.fillRect(bodyLeft, midY - radius, noseTip - bodyLeft, radius * 2);

  /* 6. Linhas de painel, o brilho especular e a tubeira. */
  context.strokeStyle = 'rgba(0,0,0,0.55)';
  context.lineWidth = 2;
  for (const x of [bodyLeft + 54, bodyLeft + 226, bodyRight - 6]) {
    context.beginPath();
    context.moveTo(x, midY - radius);
    context.lineTo(x, midY + radius);
    context.stroke();
  }
  /*
   * O brilho: numa peça preta ele não é decoração, é o que a torna visível.
   * Uma risca fina no alto do cilindro, que é onde a luz de cima bate.
   */
  context.fillStyle = 'rgba(214,222,236,0.5)';
  context.fillRect(bodyLeft + 8, midY - radius * 0.78, bodyRight - bodyLeft - 30, 4);
  context.fillStyle = 'rgba(28,30,36,1)';
  context.fillRect(bodyLeft - 16, midY - radius * 0.72, 20, radius * 1.44);
  texture.refresh();
}

/**
 * **A chama da tubeira**, que é o que faz um míssil ler-se como propulsado.
 *
 * Uma textura à parte e não assada no corpo: ela pulsa, e o corpo não. Um
 * míssil com a chama pintada teria uma chama parada - que se lê como um
 * autocolante - e fazer o corpo inteiro pulsar mudaria o tamanho de uma coisa
 * que colide.
 *
 * Desenhada da esquerda para a direita, a apagar: o lado direito encosta à
 * tubeira, o esquerdo é o fim do rasto.
 */
function makeFlameTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 256;
  const height = 96;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  const midY = height / 2;
  /* Três chamas encaixadas: a exterior mais fumo, a interior branca. */
  for (const [reach, thickness, colour, faded] of [
    [width, midY * 0.9, 'rgba(255,96,20,0.55)', 'rgba(255,96,20,0)'],
    [width * 0.72, midY * 0.58, 'rgba(255,168,60,0.75)', 'rgba(255,168,60,0)'],
    [width * 0.42, midY * 0.3, 'rgba(255,246,214,0.95)', 'rgba(255,246,214,0)'],
  ] as const) {
    const gradient = context.createLinearGradient(width - reach, 0, width, 0);
    /* A ponta do rasto escrita, e não derivada da cor por texto: uma expressão
     * regular sobre uma cor é uma maneira de a arte falhar em silêncio. */
    gradient.addColorStop(0, faded);
    gradient.addColorStop(1, colour);
    context.fillStyle = gradient;
    context.beginPath();
    context.moveTo(width, midY - thickness);
    context.quadraticCurveTo(width - reach * 0.45, midY - thickness * 0.7, width - reach, midY);
    context.quadraticCurveTo(width - reach * 0.45, midY + thickness * 0.7, width, midY + thickness);
    context.closePath();
    context.fill();
  }
  texture.refresh();
}

/**
 * **Um gerador determinista de números, para assar a turbulência.**
 *
 * Nunca `Math.random`: a textura é assada uma vez por cliente, e duas máquinas
 * que a assassem de maneiras diferentes desenhariam bolas de fogo diferentes
 * para o mesmo tiro. Isto é decoração, mas decoração que se compara em capturas
 * de ecrã - e uma arte que muda a cada arranque não se consegue aprovar.
 *
 * O gerador é o mesmo hash inteiro que a simulação usa para desempatar: barato,
 * bem distribuído, e sem estado partilhado.
 */
function bakeNoise(seed: number): number {
  let x = (seed * 1_664_525 + 1_013_904_223) % 4_294_967_296;
  x = ((x ^ (x >>> 15)) * 2_246_822_519) % 4_294_967_296;
  return ((x ^ (x >>> 13)) % 100_000) / 100_000;
}

/**
 * **As línguas de fogo**, uma coroa que gira à volta do corpo.
 *
 * Cada língua é um triângulo curvo que sai do disco e afina para fora, desenhada
 * com duas curvas quadráticas que se cruzam na ponta. Duas cópias desta textura
 * giram em sentidos contrários por cima do corpo, com contagens diferentes -
 * treze e nove - **primos entre si de propósito**: o padrão sobreposto só se
 * repete ao fim de 117 voltas, portanto nunca se vê o ciclo.
 *
 * Fundo transparente e desenhada em ADD: ela ACRESCENTA luz ao corpo em vez de
 * o substituir, que é o que uma chama faz.
 */
function makeFireTongues(
  scene: Phaser.Scene,
  key: string,
  count: number,
  seed: number,
  /**
   * **Onde nasce o anel desta coroa**, em basis points do raio da textura.
   *
   * Cada coroa traz o seu (gate V28I.B), e é ele que decide o tamanho a que ela
   * é desenhada: o diâmetro da bola a dividir por esta fracção põe a RAIZ das
   * línguas exactamente sobre a borda. Uma coroa com raio pequeno é uma coroa
   * comprida - as pontas afastam-se mais da raiz - e é assim que se fazem raios
   * longos sem os descolar da esfera.
   *
   * Era um número só, partilhado, e a terceira coroa foi esticada em vez disso:
   * multiplicar o tamanho desenhado empurra a RAIZ para fora junto com a ponta,
   * e o dono viu o resultado - "os raios estão muito longe dela".
   */
  ringBps: number,
  /** Até onde vão as pontas, em fracção do raio da textura. */
  reachTop = 0.99,
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 512;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  /*
   * **A coroa nasce na BORDA, e não dentro da bola** (gate V28H.Y).
   *
   * Ela nascia a meio do raio, e quem chamava desenhava-a pouco maior do que a
   * esfera - portanto as línguas ficavam DENTRO, e o que se via eram triângulos
   * soltos por cima de uma bola opaca. O dono disse o que elas têm de ser:
   * "que esses efeitos triangulares fiquem no limite do círculo, para parecer
   * uma coroa solar / raios solares".
   *
   * A conta que faz isso é uma só: as línguas começam a `ringBps` do raio da
   * TEXTURA, e quem desenha divide o diâmetro da bola por essa mesma fracção. O
   * anel interior cai então exactamente em cima da borda, e tudo o que se vê da
   * coroa está do lado de fora.
   */
  const inner = (middle * ringBps) / 10_000;
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    /* Comprimentos desiguais: chamas todas do mesmo tamanho leem-se como uma engrenagem. */
    const reach = middle * (reachTop - 0.21 + bakeNoise(seed + index * 5) * 0.21);
    const half = (Math.PI / count) * (0.42 + bakeNoise(seed + index * 5 + 1) * 0.3);
    const lean = (bakeNoise(seed + index * 5 + 2) - 0.5) * half * 1.4;
    const tipX = middle + Math.cos(angle + lean) * reach;
    const tipY = middle + Math.sin(angle + lean) * reach;
    const leftX = middle + Math.cos(angle - half) * inner;
    const leftY = middle + Math.sin(angle - half) * inner;
    const rightX = middle + Math.cos(angle + half) * inner;
    const rightY = middle + Math.sin(angle + half) * inner;
    const flame = context.createLinearGradient(middle, middle, tipX, tipY);
    flame.addColorStop(0, 'rgba(255,236,180,0.62)');
    flame.addColorStop(0.45, 'rgba(255,150,44,0.42)');
    flame.addColorStop(1, 'rgba(220,54,12,0)');
    context.fillStyle = flame;
    context.beginPath();
    context.moveTo(leftX, leftY);
    context.quadraticCurveTo(
      middle + Math.cos(angle - half * 0.4) * reach * 0.78,
      middle + Math.sin(angle - half * 0.4) * reach * 0.78,
      tipX,
      tipY,
    );
    context.quadraticCurveTo(
      middle + Math.cos(angle + half * 0.4) * reach * 0.78,
      middle + Math.sin(angle + half * 0.4) * reach * 0.78,
      rightX,
      rightY,
    );
    context.closePath();
    context.fill();
  }
  texture.refresh();
}

/**
 * **A esfera do Congelamento** (gate V28H.W): núcleo claro e halo azul.
 *
 * Fria por desenho e não por tinta. O que a separa da Esfera Ígnea não é a cor -
 * seria, se fosse a mesma forma pintada de azul, e o contrato proíbe isso por
 * escrito - é a ESTRUTURA: um núcleo branco pequeno, um corpo azul translúcido,
 * e facetas rectas por cima, que é o que faz gelo ler-se como gelo e fogo ler-se
 * como fogo.
 */
function makeIceSphere(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const outer = middle - 6;
  const body = context.createRadialGradient(middle, middle, 0, middle, middle, outer);
  body.addColorStop(0, 'rgba(255,255,255,1)');
  body.addColorStop(0.26, 'rgba(224,246,255,0.96)');
  body.addColorStop(0.6, 'rgba(126,206,255,0.72)');
  body.addColorStop(0.88, 'rgba(64,142,224,0.42)');
  body.addColorStop(1, 'rgba(28,86,168,0)');
  context.fillStyle = body;
  context.beginPath();
  context.arc(middle, middle, outer, 0, Math.PI * 2);
  context.fill();
  /*
   * As FACETAS: seis cunhas rectas do centro para a borda. Elas são a diferença
   * entre uma bola azul e um cristal - uma esfera de fogo não tem arestas, e
   * esta tem seis.
   */
  context.save();
  context.beginPath();
  context.arc(middle, middle, outer, 0, Math.PI * 2);
  context.clip();
  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2;
    context.fillStyle = index % 2 === 0 ? 'rgba(255,255,255,0.22)' : 'rgba(90,170,235,0.2)';
    context.beginPath();
    context.moveTo(middle, middle);
    context.lineTo(middle + Math.cos(angle) * outer, middle + Math.sin(angle) * outer);
    context.lineTo(
      middle + Math.cos(angle + Math.PI / 3) * outer,
      middle + Math.sin(angle + Math.PI / 3) * outer,
    );
    context.closePath();
    context.fill();
  }
  context.restore();
  texture.refresh();
}

/**
 * **A estrela da Lentidão** (gate V28H.W): seis pontas, e nada redondo.
 *
 * "O projéctil deve ter forma de estrela, distinguindo-se imediatamente da
 * esfera do Congelamento" - e por isso a silhueta é o que muda, não o tom. As
 * duas são azuis porque as duas são gelo; uma é uma bola e a outra tem pontas,
 * e isso lê-se de relance mesmo com três cascos no ecrã.
 */
function makeIceStar(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 256;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const long = middle - 8;
  const short = long * 0.3;
  /* Seis braços, cada um uma cunha estreita: o floco de neve clássico. */
  context.fillStyle = 'rgba(214,242,255,0.95)';
  context.beginPath();
  for (let index = 0; index < 12; index += 1) {
    const radius = index % 2 === 0 ? long : short;
    const angle = (index / 12) * Math.PI * 2;
    const x = middle + Math.cos(angle) * radius;
    const y = middle + Math.sin(angle) * radius;
    if (index === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  }
  context.closePath();
  context.fill();
  /* E um coração branco, para ela não ser uma silhueta chapada. */
  const core = context.createRadialGradient(middle, middle, 0, middle, middle, long * 0.42);
  core.addColorStop(0, 'rgba(255,255,255,1)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = core;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/**
 * **Um estilhaço de gelo**: uma lasca branca, e não uma brasa tingida.
 *
 * O rasto reaproveitava a textura da brasa da esfera de fogo com uma tinta
 * azul-clara por cima, e via-se: uma tinta MULTIPLICA, e laranja multiplicado
 * por azul-claro continua laranja. Um dardo de gelo com rasto cor de fogo.
 *
 * Branca por baixo - e por isso a tinta funciona - e em losango em vez de
 * redonda, porque o que se solta de um cristal são lascas.
 */
function makeIceShard(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 64;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const long = middle - 4;
  const wide = long * 0.42;
  const gradient = context.createLinearGradient(middle - long, middle, middle + long, middle);
  gradient.addColorStop(0, 'rgba(255,255,255,0.15)');
  gradient.addColorStop(0.5, 'rgba(255,255,255,1)');
  gradient.addColorStop(1, 'rgba(255,255,255,0.15)');
  context.fillStyle = gradient;
  context.beginPath();
  context.moveTo(middle - long, middle);
  context.lineTo(middle, middle - wide);
  context.lineTo(middle + long, middle);
  context.lineTo(middle, middle + wide);
  context.closePath();
  context.fill();
  texture.refresh();
}

/**
 * **O raio do Curto-Circuito** (gate V28H.X): uma descarga, e não uma bala.
 *
 * ## Porque ele não é uma bola nem uma estrela
 *
 * Porque as duas já existem, e as três cartas que lançam têm de se separar de
 * relance no pior caso - um 3x3 com dardos de três cartas no ar ao mesmo tempo.
 * Uma esfera é redonda, uma estrela tem pontas, e isto é uma linha PARTIDA: o
 * zigue-zague de uma descarga, que nenhuma das outras duas tem.
 *
 * Desenhado ao longo do eixo horizontal e rodado por quem o usa, como a lasca de
 * gelo: assim a quebra da linha acompanha a direcção da viagem.
 *
 * Os vértices saem de `bakeNoise` e nunca de `Math.random`, pela razão que este
 * ficheiro já escreveu: uma arte que muda a cada arranque não se consegue
 * aprovar.
 */
function makeShockBolt(scene: Phaser.Scene, key: string, seed: number): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const width = 256;
  const height = 128;
  const texture = scene.textures.createCanvas(key, width, height);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, width, height);
  const middle = height / 2;
  const steps = 9;
  const points: { x: number; y: number }[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    /* As pontas ficam no eixo e o meio parte para os lados: uma descarga. */
    const swing = Math.sin(t * Math.PI) * (height * 0.34);
    const offset = (Math.abs(bakeNoise(index * 7 + 1 + seed * 131)) * 2 - 1) * swing;
    points.push({ x: 6 + t * (width - 12), y: middle + offset });
  }
  /**
   * **A descarga AFINA para a ponta** (decisão do dono, gate V28H.X).
   *
   * Ela era um traço de espessura constante com os topos redondos, e lia-se
   * mole: "a ponta do efeito deve ficar mais pontuda, ela está muito suave".
   *
   * Duas coisas o corrigem, e as duas são geometria:
   *
   * - cada TROÇO é desenhado com a sua própria largura, e a largura cai para a
   *   frente - a descarga chega ao alvo como uma agulha e não como um tubo;
   * - os topos passam a `butt`, cortados a direito, porque um topo redondo
   *   devolve à ponta a espessura que o afinamento acabou de lhe tirar.
   *
   * E o último troço vai a zero: é o bico.
   *
   * **As DUAS pontas afinam** (segunda passagem do dono): o primeiro corte
   * deixou a traseira cheia, e um raio cortado a direito atrás lê-se como um
   * pedaço de outra coisa. A largura sobe depressa do rabo até cerca de um
   * terço e desce devagar até ao bico - é o perfil de um relâmpago, que nasce
   * fino, engrossa onde a energia se acumula e afila onde ela sai.
   */
  const draw = (base: number, colour: string) => {
    context.strokeStyle = colour;
    for (let index = 1; index < points.length; index += 1) {
      const from = points[index - 1];
      const to = points[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      /* Fina atrás, cheia a um terço, e a afilar até ao bico. */
      const t = index / (points.length - 1);
      const swell = t < 0.32 ? t / 0.32 : 1 - ((t - 0.32) / 0.68) * 0.92;
      context.lineWidth = Math.max(0.6, base * (0.1 + 0.9 * swell));
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
      context.stroke();
    }
  };
  /* Três passagens: o halo largo, o corpo, e o núcleo branco por cima. */
  context.lineCap = 'butt';
  context.lineJoin = 'miter';
  context.miterLimit = 8;
  draw(30, 'rgba(150,200,255,0.32)');
  draw(14, 'rgba(210,235,255,0.75)');
  draw(6, 'rgba(255,255,255,1)');
  /* E o BICO: um triângulo fino a fechar a ponta da frente. */
  const head = points[points.length - 1];
  const before = points[points.length - 2];
  if (head !== undefined && before !== undefined) {
    const dx = head.x - before.x;
    const dy = head.y - before.y;
    const length = Math.hypot(dx, dy) || 1;
    const nx = -dy / length;
    const ny = dx / length;
    context.fillStyle = 'rgba(255,255,255,1)';
    context.beginPath();
    context.moveTo(before.x + nx * 2.4, before.y + ny * 2.4);
    context.lineTo(head.x + (dx / length) * 14, head.y + (dy / length) * 14);
    context.lineTo(before.x - nx * 2.4, before.y - ny * 2.4);
    context.closePath();
    context.fill();
  }
  /* E duas farpas curtas a partir do meio: uma descarga ramifica-se. */
  context.strokeStyle = 'rgba(235,245,255,0.85)';
  context.lineWidth = 4;
  for (const at of [2 + (seed % 3), 5 + (seed % 3)]) {
    const from = points[at];
    const to = points[at + 1];
    if (from === undefined || to === undefined) {
      continue;
    }
    context.beginPath();
    context.moveTo(from.x, from.y);
    context.lineTo(
      from.x + (to.x - from.x) * 0.6,
      from.y + (to.y - from.y) * 0.6 - (at === 3 ? height * 0.3 : -height * 0.3),
    );
    context.stroke();
  }
  texture.refresh();
}

/** Gera as texturas, uma vez por cena. Idempotente. */
export function ensureCardBlastTextures(scene: Phaser.Scene): void {
  makeIceShard(scene, TEXTURE.iceShard);
  for (const [index, key] of SHOCK_KEYS.entries()) {
    makeShockBolt(scene, key, index);
  }
  makeIceSphere(scene, TEXTURE.iceSphere);
  makeIceStar(scene, TEXTURE.iceStar);
  /* Treze e nove: primos entre si, para as duas coroas nunca repetirem o padrão. */
  makeFireTongues(scene, TEXTURE.fireballTongues, 13, 11, CORONA_RING_BPS);
  /* A segunda é mais CURTA, e é o que a distingue: mesma raiz, pontas menores. */
  makeFireTongues(scene, TEXTURE.fireballTonguesInner, 9, 71, CORONA_RING_BPS, 0.86);
  /*
   * **A terceira coroa** (gate V28I.A): dezassete raios, longos e finos.
   *
   * Dezassete porque é primo com treze e com nove - as três giram a velocidades
   * diferentes e o padrão sobreposto não se repete dentro de uma partida - e
   * longos porque o que o dono pediu foram "mais coroas / raios solares": o que
   * faz uma coroa solar ler-se como tal são as agulhas compridas por fora das
   * chamas curtas, e não mais chamas curtas.
   */
  makeFireTongues(scene, TEXTURE.fireballRays, 17, 211, RAY_RING_BPS);
  makeRadialGlow(scene, TEXTURE.fireballCore, [
    [0, 'rgba(255,255,255,1)'],
    [0.3, 'rgba(255,226,140,0.95)'],
    [0.7, 'rgba(255,120,40,0.6)'],
    [1, 'rgba(200,40,10,0)'],
  ]);
  makeRadialGlow(scene, TEXTURE.fireballGlow, [
    [0, 'rgba(255,150,60,0.5)'],
    [0.5, 'rgba(255,90,30,0.22)'],
    [1, 'rgba(160,30,10,0)'],
  ]);
  /* O Catolé é um MÍSSIL, e já não uma segunda bola de fogo (gate V28H.V). */
  makeMissileTexture(scene, TEXTURE.catoleBody);
  makeFlameTexture(scene, TEXTURE.catoleFlame);
  makeRadialGlow(
    scene,
    TEXTURE.ember,
    [
      [0, 'rgba(255,240,200,1)'],
      [0.45, 'rgba(255,150,60,0.7)'],
      [1, 'rgba(255,90,20,0)'],
    ],
    64,
  );
  makeShockTexture(scene, TEXTURE.shock);
}

/** Uma bomba em voo, como a simulação a descreve. */
export interface CardBlastBody {
  readonly id: number;
  readonly kind: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';
  readonly x: number;
  readonly y: number;
  /** Quanto tempo ela já voa, para a esfera crescer. */
  readonly ageMs: number;
  /** O raio da explosão que ela vai fazer: o corpo é uma fracção dele. */
  readonly radius: number;
  /**
   * **O raio do CORPO**, quando quem chama o possui (gate V28H.U).
   *
   * A caça não o possui: lá o corpo é uma fracção da explosão, e cresce a
   * caminho porque a esfera acabou de nascer. O duelo possui-o - o Catolé tem um
   * raio de corpo próprio, três vezes o da Bomba Atômica, e é ele que decide
   * contra que pilastra a massa toca - e um corpo desenhado noutra medida seria
   * o desenho a discordar da colisão.
   *
   * Presente, não há crescimento: o tamanho já é a verdade no instante em que
   * ela nasce, e inchá-la a caminho fá-la-ia ler-se menor do que aquilo que
   * bate.
   */
  readonly bodyRadius?: number;
}

/** Uma explosão que acabou de acontecer. */
export interface CardBlastBurst {
  readonly id: number;
  readonly kind: 'fireball' | 'catole';
  readonly x: number;
  readonly y: number;
  /** O raio MECÂNICO: o desenho abre exactamente até onde ele feriu. */
  readonly radius: number;
}

/**
 * **As peças de UMA bola de fogo** (gate V28H.X).
 *
 * Cinco objectos e as brasas, e não um: o corpo opaco, duas coroas de línguas a
 * girar em sentidos contrários, o halo largo, e seis brasas em órbita. Guardados
 * juntos porque nascem e morrem juntos - uma bola que deixasse para trás as
 * suas brasas seria seis pontos de luz a orbitar o nada.
 */
interface FireballParts {
  /**
   * **O DISCO, e é ele que garante que a bola é opaca** (gate V28H.Y).
   *
   * Um círculo preenchido - um objecto de forma, não uma textura - e a razão é
   * que duas rondas de correcção à textura não resolveram o problema que o dono
   * continuava a ver: "está extremamente transparente". Uma textura assada pode
   * ficar transparente por meia dúzia de razões silenciosas - uma paragem de
   * degradê mal colocada, um recorte, um canvas que não subiu para a GPU - e
   * nenhuma delas dá erro. Um círculo preenchido a alfa um não tem por onde
   * falhar: ou está lá, ou não está.
   *
   * A textura de fogo continua a existir e desenha-se POR CIMA dele. O que ela
   * dá é o aspecto; o que este disco dá é a matéria.
   */
  readonly core: Phaser.GameObjects.Arc;
  /**
   * **O manto e o coração: o brilho DENTRO da esfera** (gate V28H.Y).
   *
   * Dois discos mais pequenos e mais claros, a pulsar em raio e em cor. O dono
   * viu a bola opaca e disse o que lhe faltava: "ficou um laranja muito sólido,
   * é necessário que tenha efeitos brilhantes dentro desta esfera".
   *
   * Discos, e não uma textura com o degradê pintado: a textura assada do corpo
   * foi tentada duas vezes e nunca chegou ao ecrã - e uma coisa que não se
   * consegue ver não se consegue aprovar. Três discos concêntricos dão a mesma
   * leitura - núcleo branco-amarelo, manto laranja, casca vermelha - com
   * objectos que já se provaram a desenhar.
   */
  readonly mantle: Phaser.GameObjects.Arc;
  readonly heart: Phaser.GameObjects.Arc;
  /** E o clarão quente por cima deles, que é o que os faz LUZIR. */
  readonly bloom: Phaser.GameObjects.Image;
  /** A terceira coroa: raios longos e finos, por fora das outras duas. */
  readonly rays: Phaser.GameObjects.Image;
  readonly tongues: Phaser.GameObjects.Image;
  readonly inner: Phaser.GameObjects.Image;
  readonly glow: Phaser.GameObjects.Image;
  readonly sparks: Phaser.GameObjects.Image[];
}

/**
 * **As três cores da esfera, de fora para dentro** (gate V28H.Y).
 *
 * A rampa que um degradê daria, feita com três discos: a casca é o vermelho
 * escuro da borda, o manto é o laranja de uma chama, e o coração é o
 * branco-amarelo do sítio mais quente. Elas existem porque um degradê assado
 * numa textura foi tentado duas vezes e nunca chegou a ver-se.
 */
const FIREBALL_SHELL_COLOUR = 0xb0_24_0a;
const FIREBALL_MANTLE_COLOUR = 0xf2_7e_1c;
const FIREBALL_HEART_COLOUR = 0xff_ec_b4;

/**
 * **Quantas faíscas orbitam uma bola de fogo** (gate V28I.A).
 *
 * Eram seis, grandes e alaranjadas. O dono pediu "mais partículas amarelas
 * menores", e o número e o tamanho andam ao contrário um do outro: catorze
 * pontos pequenos leem-se como brasas cuspidas, seis pontos grandes leem-se
 * como satélites.
 *
 * Continuam a não custar estado nenhum: cada uma é uma função pura do índice e
 * do relógio.
 */
const FIREBALL_SPARKS = 22;

/** O amarelo das faíscas. A textura da brasa é quente e clara, e a tinta puxa-a. */
const SPARK_TINT = 0xff_e0_66;

/**
 * **As peças de um dardo de gelo** (gate V28H.W).
 *
 * O corpo dele é o sprite da textura; isto é o que o faz LUZIR e o que o faz
 * ter rasto. Três camadas e não uma, pela mesma razão que a esfera de fogo tem
 * cinco: um sprite sozinho, por mais bem desenhado que esteja, lê-se como um
 * autocolante a deslizar - o que dá vida a um projéctil é o que ele deixa atrás
 * e o que ele acende à volta.
 */
interface IceParts {
  /** O halo, por trás do corpo: é ele que faz o dardo luzir sobre o fundo. */
  readonly glow: Phaser.GameObjects.Image;
  /** Os estilhaços, alinhados ao MOVIMENTO e a ficar para trás. */
  readonly shards: Phaser.GameObjects.Image[];
}

/** Quantos estilhaços um dardo arrasta, e quão claros eles são. */
const ICE_SHARDS = 7;
const ICE_GLOW_TINT = 0x8f_d8_ff;
const ICE_SHARD_TINT = 0xe6_f7_ff;

/** E a do raio: um azul eléctrico, mais frio e mais saturado do que o gelo. */
const SHOCK_TINT = 0x6f_b4_ff;

/** Uma explosão em curso, com o instante em que começou. */
interface LiveBurst {
  readonly kind: 'fireball' | 'catole';
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly startedAt: number;
  readonly core: Phaser.GameObjects.Image;
  readonly embers: Phaser.GameObjects.Image[];
}

/**
 * **Os corpos em voo e as duas explosões.**
 *
 * Ela não decide nada: as posições, os raios e o instante da explosão vêm da
 * simulação. O que é dela são as cores, o crescimento e o ritmo.
 */
export class CardBlastFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  /** Um corpo por bomba viva, por id, e reutilizado enquanto ela voa. */
  private readonly bodies = new Map<number, Phaser.GameObjects.Image>();
  /** A chama da tubeira de cada míssil, e o rumo a que ele vai. */
  private readonly flames = new Map<number, Phaser.GameObjects.Image>();
  private readonly heading = new Map<number, { angle: number | null; x: number; y: number }>();
  /** As esferas a carregar, por cadeira. Uma por casco, no máximo. */
  private readonly charges = new Map<string, FireballParts>();
  /** E as bolas em VOO, pela mesma composição e pelo id do projéctil. */
  private readonly flying = new Map<number, FireballParts>();
  /** As peças de brilho e rasto de cada dardo de gelo, pelo id dele. */
  private readonly frost = new Map<number, IceParts>();
  private readonly bursts: LiveBurst[] = [];
  private readonly freeCores: Phaser.GameObjects.Image[] = [];
  private readonly freeEmbers: Phaser.GameObjects.Image[] = [];
  private built = false;

  constructor(scene: Phaser.Scene, blendAdd: number, depth: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
  }

  /**
   * Desenha as bombas que existem, e apaga os corpos das que já não existem.
   *
   * Uma bomba que sai da lista rebentou ou perdeu o alvo; em qualquer dos casos
   * o corpo dela deixa de ser desenhado no mesmo quadro - a explosão vem do
   * evento, e não de uma suposição feita aqui.
   */
  update(bodies: readonly CardBlastBody[], nowMs: number): void {
    if (bodies.length > 0) {
      this.build();
    }
    const present = new Set<number>();
    for (const body of bodies) {
      present.add(body.id);
      /*
       * O corpo é uma fracção do raio da explosão, e a esfera CRESCE a caminho.
       * O Catolé não cresce: ele é uma massa, e uma massa que inchasse a voar
       * leria como carga em vez de peso.
       */
      const grown =
        body.kind === 'fireball'
          ? FIREBALL_BIRTH + (1 - FIREBALL_BIRTH) * Math.min(1, body.ageMs / FIREBALL_GROW_MS)
          : 1;
      const size =
        body.bodyRadius === undefined
          ? body.radius * (body.kind === 'catole' ? 0.34 : CARD_FIREBALL_BODY_FRACTION) * 2 * grown
          : body.bodyRadius * 2;
      let sprite = this.bodies.get(body.id);
      if (sprite === undefined) {
        sprite = this.takeBody(body.kind);
        this.bodies.set(body.id, sprite);
      }
      /*
       * **O RUMO, medido entre duas posições desenhadas** (gate V28H.V).
       *
       * Um míssil tem nariz, e um nariz apontado para o sítio errado é pior do
       * que nenhum. A direcção é medida e nunca adivinhada: a bomba curva atrás
       * do alvo, portanto uma direcção calculada a partir do sítio onde ela
       * NASCEU deixaria de ser verdade no primeiro desvio.
       *
       * Enquanto não houver duas posições não há direcção nenhuma, e o corpo
       * fica escondido esse quadro - dezasseis milésimos. Desenhá-lo a apontar
       * para a direita seria mostrar um míssil de través, e isso vê-se.
       */
      const before = this.heading.get(body.id);
      const moved = before === undefined ? null : { dx: body.x - before.x, dy: body.y - before.y };
      const angle =
        moved !== null && (moved.dx !== 0 || moved.dy !== 0)
          ? Math.atan2(moved.dy, moved.dx)
          : (before?.angle ?? null);
      this.heading.set(body.id, { angle, x: body.x, y: body.y });
      if (body.kind === 'catole' && angle === null) {
        sprite.setVisible(false);
        this.flames.get(body.id)?.setVisible(false);
        continue;
      }
      sprite.setVisible(true).setPosition(body.x, body.y);
      if (body.kind === 'catole') {
        /* O corpo do míssil é o sprite; a esfera é a composição, mais abaixo. */
        /*
         * **Alongado, e nunca um círculo.** O corpo mecânico é uma
         * circunferência - é ela que o testa contra uma pilastra - e a
         * silhueta desenhada é um míssil: comprido três raios, largo menos de
         * um. A largura cabe dentro do círculo, que é o que impede o desenho de
         * prometer um alcance de colisão que ele não tem.
         */
        sprite
          .setRotation(angle ?? 0)
          .setDisplaySize(size * MISSILE_LENGTH, size * MISSILE_WIDTH)
          .setAlpha(1);
      } else if (body.kind === 'shock_bolt') {
        /*
         * **O RAIO** (gate V28H.X): deitado ao longo da viagem, e a tremer.
         *
         * Sem rumo medido ainda - o primeiro quadro - ele fica escondido, como o
         * Catolé: uma descarga desenhada de través aponta para o sítio errado, e
         * isso vê-se mais do que a ausência de um quadro.
         *
         * O tremor é de ESCALA e não de posição: a linha estica e encolhe no
         * comprimento, que é o que faz uma descarga parecer viva sem a fazer
         * saltar para fora da trajectória que a sala decidiu.
         */
        if (angle === null) {
          sprite.setVisible(false);
          this.frost.get(body.id)?.glow.setVisible(false);
          continue;
        }
        const reach = Math.max(8, size);
        /*
         * **A descarga REFAZ-SE** (gate V28H.X): outra chapa a cada 40 ms.
         *
         * O quadro sai do relógio da cena e do ID do projéctil - dois raios no
         * ar ao mesmo tempo não crepitam em uníssono, que seria a leitura de uma
         * animação e não de duas descargas - e a chapa é trocada no sprite que
         * já existe, sem criar nem destruir nada.
         *
         * Por cima disso: o comprimento estica e encolhe, e a linha inclina-se
         * um pouco para cada lado. As três coisas juntas são o que faz uma
         * descarga parecer viva sem a tirar da trajectória que a sala decidiu.
         */
        const frame = (Math.floor(nowMs / SHOCK_FRAME_MS) + body.id) % SHOCK_FRAMES;
        const key = SHOCK_KEYS[frame];
        if (key !== undefined && sprite.texture.key !== key) {
          sprite.setTexture(key);
        }
        const flicker = 0.82 + Math.abs(Math.sin(nowMs / 37)) * 0.36;
        const lean = Math.sin(nowMs / 53 + body.id) * 0.12;
        sprite
          /*
           * **Trinta por cento mais comprido, duas vezes** (decisão do dono,
           * gate V28H.X): 1,6 -> 2,08 -> 2,7 do corpo. Só o COMPRIMENTO - a
           * espessura fica onde estava, senão o raio engorda em vez de esticar.
           */
          .setDisplaySize(reach * 2.7 * flicker, reach * 0.8 * (1.15 - flicker * 0.25))
          .setAlpha(0.85 + Math.abs(Math.sin(nowMs / 31)) * 0.15)
          .setRotation(angle + lean);
        let arc = this.frost.get(body.id);
        if (arc === undefined) {
          arc = this.takeIce();
          this.frost.set(body.id, arc);
        }
        /* O mesmo halo e as mesmas lascas dos dardos de gelo, noutra cor. */
        this.paintIce(arc, body.x, body.y, reach, angle, nowMs, SHOCK_TINT);
      } else if (body.kind === 'ice_sphere' || body.kind === 'ice_star') {
        /*
         * **Os dois dardos de gelo** (gate V28H.W): um sprite só, e chega.
         *
         * Eles não são uma composição como a esfera de fogo porque não têm nada
         * para compor - são pequenos, atravessam o ecrã em pouco mais de um
         * segundo, e o que têm de dizer é uma silhueta: bola ou estrela.
         *
         * A estrela GIRA e a esfera pulsa, e é a segunda coisa que as separa em
         * movimento - quem vê de relance não tem de comparar formas.
         */
        const bolt = Math.max(6, size);
        sprite
          .setDisplaySize(
            /* A esfera respira; a estrela mantém a forma e roda. */
            body.kind === 'ice_sphere' ? bolt * (1 + Math.sin(nowMs / 110) * 0.06) : bolt,
            bolt,
          )
          /*
           * **Opaco** (gate V28H.W): estava a 0,92 e lia-se lavado sobre o fundo
           * escuro da arena. Um dardo de gelo é uma pedra e não um vidro.
           */
          .setAlpha(1)
          .setRotation(body.kind === 'ice_star' ? nowMs / 260 : 0);
        let ice = this.frost.get(body.id);
        if (ice === undefined) {
          ice = this.takeIce();
          this.frost.set(body.id, ice);
        }
        this.paintIce(ice, body.x, body.y, bolt, angle, nowMs, ICE_GLOW_TINT);
      } else {
        /*
         * **A esfera é uma COMPOSIÇÃO** (gate V28H.X), e não este sprite.
         *
         * Ele fica escondido - o conjunto de corpos continua a ser o que sabe
         * que esta bomba existe, e é ele que a apaga quando a sala a deixa de
         * mandar - e o que se vê são as cinco camadas que `paintFireball`
         * desenha. Uma mancha radial somada sobre o fundo lia-se como vidro
         * aceso, que foi o que o dono reprovou.
         */
        sprite.setVisible(false);
        let parts = this.flying.get(body.id);
        if (parts === undefined) {
          parts = this.takeFireball();
          this.flying.set(body.id, parts);
        }
        this.paintFireball(parts, body.x, body.y, size / 2, nowMs);
      }
      if (body.kind === 'catole') {
        /*
         * E a CHAMA, atrás da tubeira: ela é o que diz que aquilo é propulsado
         * e não atirado. Pulsa depressa - uma chama parada é um autocolante - e
         * o pulso é do relógio, portanto dois clientes com o mesmo relógio de
         * apresentação veem a mesma chama.
         */
        let flame = this.flames.get(body.id);
        if (flame === undefined) {
          flame = this.scene.add
            .image(0, 0, TEXTURE.catoleFlame)
            .setDepth(this.depth + 1)
            .setBlendMode(this.blendAdd)
            /* A âncora à DIREITA: a chama encosta à tubeira e cresce para trás. */
            .setOrigin(1, 0.5);
          this.flames.set(body.id, flame);
        }
        const tail = (size * MISSILE_LENGTH) / 2;
        const facing = angle ?? 0;
        const pulse = 0.78 + Math.sin(nowMs / 55) * 0.22;
        flame
          .setVisible(true)
          .setPosition(body.x - Math.cos(facing) * tail, body.y - Math.sin(facing) * tail)
          .setRotation(facing)
          .setDisplaySize(size * MISSILE_LENGTH * 0.72 * pulse, size * MISSILE_WIDTH * 0.9)
          .setAlpha(pulse);
        /*
         * **Sem anel de guia** (gate V28H.W).
         *
         * Aqui girava um anel à volta da bomba, para ela se ler como teleguiada
         * e não como pedra. O dono viu o míssil e mandou-o embora: "remova os
         * círculos de indicação ao redor do míssil da bomba". E a razão pela
         * qual ele existia caiu sozinha - um míssil com aletas, ogiva e chama já
         * diz que é dirigido, e o anel passou a ser um segundo objecto a dizer o
         * que a silhueta já dizia.
         */
      }
    }
    for (const [id, sprite] of [...this.bodies]) {
      if (present.has(id)) {
        continue;
      }
      sprite.destroy();
      this.bodies.delete(id);
      this.flames.get(id)?.destroy();
      this.flames.delete(id);
      this.heading.delete(id);
      const parts = this.flying.get(id);
      if (parts !== undefined) {
        CardBlastFx.releaseFireball(parts);
        this.flying.delete(id);
      }
      const ice = this.frost.get(id);
      if (ice !== undefined) {
        ice.glow.setVisible(false);
        for (const shard of ice.shards) {
          shard.setVisible(false);
        }
        ice.glow.destroy();
        for (const shard of ice.shards) {
          shard.destroy();
        }
        this.frost.delete(id);
      }
    }
    this.advanceBursts(nowMs);
  }

  /**
   * **Uma explosão aconteceu.**
   *
   * O raio é o MECÂNICO, o mesmo que decidiu quem levou dano - portanto a onda
   * desenhada é a verdade e não uma aproximação dela. O tipo vem do evento e
   * nunca é adivinhado pelo tamanho: adivinhar pela medida seria a cena a
   * decidir a arte a partir de um número de equilíbrio.
   */
  burst(event: CardBlastBurst, nowMs: number): void {
    this.build();
    const core = this.freeCores.pop();
    if (core === undefined) {
      return;
    }
    const embers: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < EMBERS; index += 1) {
      const ember = this.freeEmbers.pop();
      if (ember === undefined) {
        break;
      }
      embers.push(ember);
    }
    core
      .setTexture(event.kind === 'catole' ? TEXTURE.shock : TEXTURE.fireballCore)
      .setVisible(true)
      .setPosition(event.x, event.y)
      .setDisplaySize(0, 0)
      .setAlpha(1);
    this.bursts.push({
      kind: event.kind,
      x: event.x,
      y: event.y,
      radius: event.radius,
      startedAt: nowMs,
      core,
      embers,
    });
  }

  /** Abre e apaga as explosões vivas. Nada é alocado aqui. */
  private advanceBursts(nowMs: number): void {
    for (let index = this.bursts.length - 1; index >= 0; index -= 1) {
      const burst = this.bursts[index];
      if (burst === undefined) {
        continue;
      }
      const life = (nowMs - burst.startedAt) / BURST_MS;
      if (life >= 1) {
        burst.core.setVisible(false);
        this.freeCores.push(burst.core);
        for (const ember of burst.embers) {
          ember.setVisible(false);
          this.freeEmbers.push(ember);
        }
        this.bursts.splice(index, 1);
        continue;
      }
      /*
       * Abre depressa e apaga devagar: uma explosão que crescesse linearmente
       * pareceria uma bolha a inflar em vez de uma coisa a rebentar.
       */
      const opened = 1 - (1 - life) ** 3;
      const diameter = burst.radius * 2 * opened;
      burst.core
        .setPosition(burst.x, burst.y)
        .setDisplaySize(diameter, diameter)
        .setAlpha(1 - life ** 2);
      for (const [order, ember] of burst.embers.entries()) {
        const angle = (order / EMBERS) * Math.PI * 2 + burst.startedAt;
        const distance = burst.radius * (0.35 + opened * 0.75);
        const dot = burst.radius * 0.09 * (1 - life);
        ember
          .setVisible(true)
          .setPosition(burst.x + Math.cos(angle) * distance, burst.y + Math.sin(angle) * distance)
          .setDisplaySize(Math.max(2, dot * 2), Math.max(2, dot * 2))
          .setAlpha(1 - life);
      }
    }
  }

  /**
   * **A esfera que ainda está a ser CARREGADA, à frente da boca** (gate V28H.U).
   *
   * Ela não é uma bomba: não voa, não fere e não tem id de projétil - existe
   * enquanto um dedo está em cima de uma tecla. Por isso não entra em
   * {@link update}, que fala de coisas que a sala possui, e é endereçada pela
   * cadeira: uma por casco, no máximo.
   *
   * O tamanho é o que a esfera VAI ter, multiplicado pela fracção já cumprida:
   * o que se vê a crescer é a promessa, e ela é honesta porque sai do mesmo
   * número que a sala vai cobrar quando o dedo sair.
   *
   * Uma lista vazia apaga as que havia sem destruir nada: o ciclo é de segundos.
   */
  charging(
    spheres: readonly {
      readonly key: string;
      readonly x: number;
      readonly y: number;
      /** O raio que ela terá se soltar agora. */
      readonly radius: number;
    }[],
    nowMs: number,
  ): void {
    if (spheres.length > 0) {
      this.build();
    }
    const present = new Set<string>();
    for (const sphere of spheres) {
      present.add(sphere.key);
      let parts = this.charges.get(sphere.key);
      if (parts === undefined) {
        parts = this.takeFireball();
        this.charges.set(sphere.key, parts);
      }
      /* A MESMA composição da que voa: elas são a mesma bola em dois momentos. */
      this.paintFireball(parts, sphere.x, sphere.y, sphere.radius, nowMs);
    }
    for (const [key, parts] of [...this.charges]) {
      if (present.has(key)) {
        continue;
      }
      CardBlastFx.releaseFireball(parts);
      this.charges.delete(key);
    }
  }

  /**
   * **Uma bola de fogo, desenhada** (gate V28H.X).
   *
   * Um sítio só para as duas que existem - a que voa e a que está a ser
   * carregada - porque elas são a mesma coisa em momentos diferentes, e duas
   * cópias deste código seriam duas bolas que divergem no dia em que uma delas
   * for afinada.
   *
   * O que aqui se decide é o RITMO, e nada mais: o tamanho vem de quem chama, e
   * quem chama tira-o do raio que a sala vai cobrar.
   */
  private paintFireball(
    parts: FireballParts,
    x: number,
    y: number,
    radius: number,
    nowMs: number,
  ): void {
    const diameter = Math.max(2, radius * 2);
    /*
     * O corpo **não pulsa de todo** (gate V28H.Y): alfa um, sempre. Ele é a
     * massa, e uma massa que oscila de opacidade é uma massa que pisca. O que
     * respira são as chamas por cima dele, que é onde o movimento pertence.
     */
    /*
     * A casca primeiro: alfa um, sem pulsar, e do tamanho exacto da bola. É ela
     * a silhueta - a borda que se vê é a dela - e é por isso que a bola é
     * NÍTIDA em vez de esfumada.
     */
    parts.core.setVisible(true).setPosition(x, y).setRadius(radius).setAlpha(1);
    /*
     * E o interior, que é o que ela não tinha: um manto laranja e um coração
     * quase branco, os dois a PULSAR em raio e em ritmos diferentes.
     *
     * Ritmos diferentes de propósito - 240 ms e 170 ms - porque dois discos a
     * respirar ao mesmo tempo leem-se como um só objecto a mudar de tamanho, e
     * o que se quer é fogo a mexer lá dentro.
     */
    parts.mantle
      .setVisible(true)
      .setPosition(x, y)
      .setRadius(radius * (0.66 + Math.sin(nowMs / 240) * 0.07))
      .setAlpha(0.95);
    parts.heart
      .setVisible(true)
      .setPosition(x, y)
      .setRadius(radius * (0.3 + Math.sin(nowMs / 170 + 1.3) * 0.06))
      .setAlpha(0.92 + Math.sin(nowMs / 110) * 0.08);
    /*
     * E o clarão por cima dos três, somado: é ele que transforma discos de cor
     * em qualquer coisa que emite luz. Contido à esfera - 1,02 do diâmetro -
     * porque o que ele ilumina é o interior dela, e não o espaço à volta.
     */
    parts.bloom
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter * 1.02, diameter * 1.02)
      .setAlpha(0.5 + Math.sin(nowMs / 130) * 0.14);
    /*
     * As duas coroas, em sentidos CONTRÁRIOS e a velocidades diferentes. É o
     * cruzamento delas que produz o borbulhar - duas texturas fixas a girar
     * dão uma imagem que nunca se repete, e custam dois objectos.
     */
    /*
     * **A COROA**, e o tamanho não é escolhido: é o diâmetro a dividir pela
     * fracção a que as línguas nascem na textura. Assim o anel interior delas
     * cai exactamente sobre a borda da esfera, e os raios ficam todos de fora -
     * que é a diferença entre uma coroa solar e triângulos dentro de uma bola.
     */
    const corona = diameter / (CORONA_RING_BPS / 10_000);
    /* E os raios longos pelo anel DELES, que é o que os cola à borda. */
    const rayCorona = diameter / (RAY_RING_BPS / 10_000);
    parts.tongues
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(corona, corona)
      .setAlpha(0.78 + Math.sin(nowMs / 90) * 0.18)
      .setRotation(nowMs / 900);
    /*
     * A terceira, por FORA das duas: agulhas compridas a rodar devagar, no
     * sentido da primeira. É a mais fraca das três de propósito - ela dá o
     * alcance da coroa, e não o brilho dela.
     */
    parts.rays
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(rayCorona, rayCorona)
      .setAlpha(0.46 + Math.sin(nowMs / 143) * 0.14)
      .setRotation(nowMs / 2_100);
    /*
     * A segunda coroa, em sentido contrário: os raios cintilam. Ela é curta
     * porque as PONTAS dela são curtas na textura, e não porque é desenhada mais
     * pequena - encolhê-la enterrava a raiz dentro da bola, que é o mesmo
     * defeito da terceira ao contrário.
     */
    parts.inner
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(corona, corona)
      .setAlpha(0.66 + Math.sin(nowMs / 61 + 2) * 0.2)
      .setRotation(-nowMs / 1_450);
    /*
     * O halo: **estreito e fraco** (gate V28H.Y).
     *
     * Era largo - duas vezes e um décimo o diâmetro - e a 0,34 de alfa. Somado
     * sobre o fundo, ele era metade do que se via da bola, e uma nuvem larga a
     * toda a volta é exactamente o que faz uma coisa parecer esfumada. Ele agora
     * acompanha a borda em vez de a substituir.
     */
    parts.glow
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(diameter * 1.4, diameter * 1.4)
      .setAlpha(0.22 + Math.sin(nowMs / 210) * 0.06);
    /*
     * E as brasas, em órbita. Cada uma é uma função pura do índice e do relógio
     * - sem posição guardada e sem alocação - e sobem e descem em raio, o que
     * as faz ler-se como cuspidas em vez de presas a um carrossel.
     */
    for (const [index, spark] of parts.sparks.entries()) {
      /*
       * Duas famílias, pelo índice par ou ímpar: uma rente à borda e outra a
       * dispersar-se pela coroa. Sem as duas, catorze pontos à mesma distância
       * desenham um colar - e o que se quer são brasas.
       *
       * As velocidades angulares também diferem - o índice entra no divisor -
       * portanto elas nunca se alinham, que é a outra metade do colar.
       */
      const family = index % 3;
      const phase =
        nowMs / (family === 0 ? 760 : family === 1 ? 520 : 410) +
        (index / FIREBALL_SPARKS) * Math.PI * 2 +
        index * 0.7;
      /*
       * TRÊS famílias (gate V28I.B): rente à borda, dispersa pela coroa, e uma
       * terceira mais longe e mais fina. Duas famílias já não chegavam com vinte
       * e duas faíscas - elas voltavam a desenhar dois colares - e o que o dono
       * pediu foram "mais partículas pequenas brilhantes", não mais anéis.
       */
      const base = family === 0 ? 0.9 : family === 1 ? 1.2 : 1.52;
      const orbit = radius * (base + 0.18 * Math.sin(nowMs / (280 + index * 17) + index * 1.7));
      /* Pequenas, e as de fora ainda mais: é o tamanho que as faz faíscas. */
      const scale = family === 2 ? 0.036 : 0.055;
      const dot = Math.max(2, radius * scale * (0.62 + 0.38 * Math.sin(nowMs / 150 + index)));
      spark
        .setVisible(true)
        .setPosition(x + Math.cos(phase) * orbit, y + Math.sin(phase) * orbit)
        .setDisplaySize(dot, dot)
        .setTint(SPARK_TINT)
        .setAlpha(0.5 + 0.4 * Math.sin(nowMs / 190 + index * 2.1));
    }
  }

  /** Cria as peças de uma bola. Elas são escondidas, nunca recriadas por quadro. */
  private takeFireball(): FireballParts {
    const layer = (key: string, depth: number, blend: number) =>
      this.scene.add.image(0, 0, key).setDepth(depth).setBlendMode(blend);
    const sparks: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < FIREBALL_SPARKS; index += 1) {
      sparks.push(layer(TEXTURE.ember, this.depth + 7, this.blendAdd));
    }
    return {
      /* O halo por baixo de tudo; o disco e a textura em NORMAL por cima dele. */
      glow: layer(TEXTURE.fireballGlow, this.depth + 1, this.blendAdd),
      core: this.scene.add
        .circle(0, 0, 1, FIREBALL_SHELL_COLOUR, 1)
        .setDepth(this.depth + 2)
        .setBlendMode(BLEND_NORMAL),
      mantle: this.scene.add
        .circle(0, 0, 1, FIREBALL_MANTLE_COLOUR, 1)
        .setDepth(this.depth + 3)
        .setBlendMode(BLEND_NORMAL),
      heart: this.scene.add
        .circle(0, 0, 1, FIREBALL_HEART_COLOUR, 1)
        .setDepth(this.depth + 4)
        .setBlendMode(BLEND_NORMAL),
      bloom: layer(TEXTURE.fireballCore, this.depth + 5, this.blendAdd),
      rays: layer(TEXTURE.fireballRays, this.depth + 6, this.blendAdd),
      inner: layer(TEXTURE.fireballTonguesInner, this.depth + 6, this.blendAdd),
      tongues: layer(TEXTURE.fireballTongues, this.depth + 6, this.blendAdd),
      sparks,
    };
  }

  private static releaseFireball(parts: FireballParts): void {
    parts.core.destroy();
    parts.mantle.destroy();
    parts.heart.destroy();
    parts.bloom.destroy();
    parts.rays.destroy();
    parts.tongues.destroy();
    parts.inner.destroy();
    parts.glow.destroy();
    for (const spark of parts.sparks) {
      spark.destroy();
    }
  }

  /**
   * **O brilho e o rasto de um dardo de gelo.**
   *
   * Um halo e sete estilhaços, criados à primeira vez que este dardo é
   * desenhado e largados quando a sala deixa de o mandar - a mesma disciplina
   * do resto desta classe.
   */
  private takeIce(): IceParts {
    const shards: Phaser.GameObjects.Image[] = [];
    for (let index = 0; index < ICE_SHARDS; index += 1) {
      shards.push(
        this.scene.add
          .image(0, 0, TEXTURE.iceShard)
          .setDepth(this.depth + 1)
          .setBlendMode(this.blendAdd)
          .setTint(ICE_SHARD_TINT),
      );
    }
    return {
      glow: this.scene.add
        .image(0, 0, TEXTURE.fireballGlow)
        .setDepth(this.depth)
        .setBlendMode(this.blendAdd)
        .setTint(ICE_GLOW_TINT),
      shards,
    };
  }

  /**
   * **O halo à volta do dardo e os estilhaços atrás dele.**
   *
   * Os estilhaços vão para trás ao longo do RUMO medido - o mesmo que aponta o
   * nariz do Catolé - e não numa direcção inventada: é isso que os faz ler-se
   * como pedaços que se soltaram e ficaram, e não como uma nuvem colada ao
   * corpo. Sem rumo ainda medido, no primeiro quadro, eles não aparecem.
   *
   * Nenhum deles guarda estado: a posição de cada um é uma função do índice, do
   * relógio e do sítio onde o dardo está agora. É a mesma decisão das faíscas da
   * esfera de fogo, e pela mesma razão - um rasto com memória obrigaria a
   * limpá-lo em cada morte, reconstrução e SHUTDOWN.
   */
  private paintIce(
    parts: IceParts,
    x: number,
    y: number,
    bolt: number,
    angle: number | null,
    nowMs: number,
    tint: number,
  ): void {
    /* Proporcional ao dardo, e mais contido agora que ele é três vezes maior. */
    const halo = bolt * (1.55 + Math.sin(nowMs / 130) * 0.12);
    parts.glow
      .setVisible(true)
      .setPosition(x, y)
      .setDisplaySize(halo, halo)
      .setTint(tint)
      .setAlpha(0.5 + Math.sin(nowMs / 190) * 0.1);
    if (angle === null) {
      for (const shard of parts.shards) {
        shard.setVisible(false);
      }
      return;
    }
    const back = { x: -Math.cos(angle), y: -Math.sin(angle) };
    /* A perpendicular, para os estilhaços se abrirem em leque atrás do dardo. */
    const side = { x: -back.y, y: back.x };
    for (const [index, shard] of parts.shards.entries()) {
      /* Escalonados no tempo: cada um corre o seu troço do rasto e recomeça. */
      const life = (((nowMs / 620 + index / ICE_SHARDS) % 1) + 1) % 1;
      const along = bolt * (0.35 + life * 1.5);
      const spread = bolt * 0.3 * (index % 2 === 0 ? 1 : -1) * life;
      const size = bolt * 0.3 * (1 - life * 0.7);
      shard
        .setTint(tint === ICE_GLOW_TINT ? ICE_SHARD_TINT : tint)
        .setVisible(true)
        .setPosition(x + back.x * along + side.x * spread, y + back.y * along + side.y * spread)
        /* Deitadas ao longo da viagem: uma lasca atravessada leria-se como sujidade. */
        .setDisplaySize(size * 2.1, size * 0.7)
        .setRotation(angle)
        .setAlpha((1 - life) * 0.9);
    }
  }

  private takeBody(kind: CardBlastBody['kind']): Phaser.GameObjects.Image {
    const key =
      kind === 'catole'
        ? TEXTURE.catoleBody
        : kind === 'ice_sphere'
          ? TEXTURE.iceSphere
          : kind === 'ice_star'
            ? TEXTURE.iceStar
            : kind === 'shock_bolt'
              ? (SHOCK_KEYS[0] ?? TEXTURE.fireballCore)
              : TEXTURE.fireballCore;
    return (
      this.scene.add
        .image(0, 0, key)
        .setDepth(this.depth + 2)
        /*
         * O míssil em NORMAL, a esfera em ADD: uma é metal pintado e a outra é
         * fogo. Somar luz sobre o metal tornaria o escuro transparente, e o
         * volume que a textura assou desaparecia.
         */
        /*
         * **O gelo em NORMAL, como o metal** (gate V28H.W).
         *
         * Ele estava em ADD e via-se: somar luz sobre o fundo escuro da arena
         * lava as cores claras umas nas outras, e um cristal branco-azul somado
         * perde exactamente aquilo que o faz ser um cristal - as arestas. O
         * brilho passou para uma camada própria, por trás, que é onde ele não
         * come a silhueta.
         */
        .setBlendMode(
          kind === 'catole' || kind === 'ice_sphere' || kind === 'ice_star'
            ? BLEND_NORMAL
            : /* O raio e a esfera de fogo SOMAM: são luz, e não corpos sólidos. */
              this.blendAdd,
        )
    );
  }

  private build(): void {
    if (this.built) {
      return;
    }
    this.built = true;
    ensureCardBlastTextures(this.scene);
    for (let index = 0; index < BURST_POOL; index += 1) {
      this.freeCores.push(
        this.scene.add
          .image(0, 0, TEXTURE.fireballCore)
          .setDepth(this.depth + 3)
          .setBlendMode(this.blendAdd)
          .setVisible(false),
      );
    }
    for (let index = 0; index < BURST_POOL * EMBERS; index += 1) {
      this.freeEmbers.push(
        this.scene.add
          .image(0, 0, TEXTURE.ember)
          .setDepth(this.depth + 4)
          .setBlendMode(this.blendAdd)
          .setVisible(false),
      );
    }
  }

  destroy(): void {
    for (const sprite of this.bodies.values()) {
      sprite.destroy();
    }
    this.bodies.clear();
    for (const flame of this.flames.values()) {
      flame.destroy();
    }
    this.flames.clear();
    for (const parts of this.flying.values()) {
      CardBlastFx.releaseFireball(parts);
    }
    this.flying.clear();
    for (const parts of this.charges.values()) {
      CardBlastFx.releaseFireball(parts);
    }
    this.charges.clear();
    /* E o brilho e o rasto dos dardos de gelo, pela mesma regra (V28H.W). */
    for (const parts of this.frost.values()) {
      parts.glow.destroy();
      for (const shard of parts.shards) {
        shard.destroy();
      }
    }
    this.frost.clear();
    this.heading.clear();
    for (const burst of this.bursts) {
      burst.core.destroy();
      for (const ember of burst.embers) {
        ember.destroy();
      }
    }
    this.bursts.length = 0;
    for (const core of this.freeCores) {
      core.destroy();
    }
    this.freeCores.length = 0;
    for (const ember of this.freeEmbers) {
      ember.destroy();
    }
    this.freeEmbers.length = 0;
    this.built = false;
  }
}

/**
 * **A fracção do raio da explosão que o CORPO da esfera ocupa.**
 *
 * Já não é decidida aqui (gate V28H.Z): ela vive em `CARD_SPHERE_BODY_BPS`, no
 * domínio, porque passou a decidir também com que raio a esfera APANHA. Enquanto
 * foi um número de arte, a bola desenhada e a bola que colide eram duas coisas
 * diferentes - e uma bola de setenta cascos passava por cima de uma nave sem
 * rebentar.
 *
 * Fica o nome, que é o que esta cena lê, e o valor vem de lá.
 */
export const CARD_FIREBALL_BODY_FRACTION = CARD_SPHERE_BODY_BPS / 10_000;

/**
 * **O tremor do Catolé: curto, e igual nos dois modos.**
 *
 * Ele é a maior explosão do jogo, e o peso dela precisa de ser sentido e não só
 * visto. Curto de propósito: um tremor longo faz perder o alvo, e uma carta que
 * atrapalha quem a usou é uma carta que ninguém volta a usar.
 *
 * Aqui e não em cada cena: os dois números estavam escritos na caça, e o duelo
 * teria escrito os seus - dois tremores diferentes para a mesma explosão.
 */
export const CATOLE_SHAKE_MS = 180;
export const CATOLE_SHAKE_INTENSITY = 0.006;

/** Quantos corpos em voo o conjunto suporta antes de alocar. Só para provas. */
export const CARD_BLAST_BODY_POOL = BODY_POOL;
